import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Skeleton } from '../ui';
import { MissionFilters } from './MissionFilters';
import { MissionPaths } from './MissionPaths';
import { MissionSection } from './MissionSection';
import { listDrafts, missionErrorKeys, useMissions } from '../../context/MissionContext';

/**
 * Mission Center (spec §10.3) — the learning product's home.
 *
 * Order is fixed and intentional: what is due today, then the onboarding
 * missions someone was assigned, then the voluntary challenges, then whatever
 * else exists. Nothing on this screen ranks people, awards points or threatens
 * a streak (spec §2.3); the only aggregate that claims competence is the
 * "secure" count, and that one is earned under the mastery model's evidence
 * rules rather than by showing up.
 */
export function MissionCenter() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    filteredMissions, dueIds, counts, topics, loading, error, offline, filters, setFilters, refresh,
  } = useMissions();

  // One localStorage scan for the whole screen rather than one per card.
  const draftIds = useMemo(
    () => new Set(listDrafts().filter((draft) => !draft.completed).map((draft) => draft.missionId)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredMissions]
  );

  const groups = useMemo(() => {
    const due = [];
    const onboarding = [];
    const challenge = [];
    const other = [];
    for (const mission of filteredMissions) {
      if (dueIds.has(mission.id)) due.push(mission);
      else if (mission.origin === 'onboarding') onboarding.push(mission);
      else if (mission.origin === 'challenge') challenge.push(mission);
      else other.push(mission);
    }
    return { due, onboarding, challenge, other };
  }, [filteredMissions, dueIds]);

  const startMission = (mission) => navigate(`/missions/run/${mission.id}`);

  const isSessionProblem =
    error && ['session_expired', 'permission_denied', 'app_not_licensed', 'tenant_suspended'].includes(error.code);

  return (
    <div className="etch-view">
      <div className="etch-column" data-width="wide">
        <header className="etch-page-header">
          <div className="etch-page-header__title">
            <span className="etch-eyebrow">{t('nav.missions')}</span>
            <h1>{t('missions.title')}</h1>
            <p className="etch-mission-center__subtitle">{t('missions.subtitle')}</p>
          </div>
          <div className="etch-page-header__actions">
            <Button variant="secondary" size="compact" onClick={() => navigate('/missions/mastery')}>
              {t('mastery.overviewLink')}
            </Button>
            <Button variant="ghost" size="compact" onClick={() => navigate('/missions/decks')}>
              {t('learn.decks')}
            </Button>
          </div>
        </header>

        {offline && (
          <Alert tone="warning" title={t('states.offline')}>
            {t('missions.offlineCenterBody')}
          </Alert>
        )}

        {error && (
          <Alert tone={isSessionProblem ? 'warning' : 'danger'} title={t(missionErrorKeys(error))}>
            <p>{t('missions.centerFailedBody')}</p>
            {!isSessionProblem && (
              <Button variant="secondary" size="compact" onClick={() => refresh()}>
                {t('states.retry')}
              </Button>
            )}
          </Alert>
        )}

        {counts && !error && (
          <dl className="etch-mission-counts">
            <div className="etch-mission-counts__item" data-kind="due">
              <dt>{t('missions.dueToday')}</dt>
              <dd>{counts.due}</dd>
            </div>
            <div className="etch-mission-counts__item" data-kind="onboarding">
              <dt>{t('missions.onboarding')}</dt>
              <dd>{counts.onboarding}</dd>
            </div>
            <div className="etch-mission-counts__item" data-kind="challenge">
              <dt>{t('missions.challenges')}</dt>
              <dd>{counts.challenge}</dd>
            </div>
            <div className="etch-mission-counts__item" data-kind="secure">
              <dt>{t('mastery.level.secure')}</dt>
              <dd>{counts.secure}</dd>
            </div>
          </dl>
        )}

        <MissionFilters
          filters={filters}
          topics={topics}
          onChange={setFilters}
          resultCount={filteredMissions.length}
        />

        {loading ? (
          <div className="etch-stack" data-gap="lg">
            <p className="etch-visually-hidden" role="status">
              {t('states.loading')}
            </p>
            <Skeleton height="1.25rem" width="40%" />
            <Skeleton height="9rem" />
            <Skeleton height="9rem" />
          </div>
        ) : (
          <div className="etch-stack" data-gap="lg">
            <MissionSection
              title={t('missions.dueToday')}
              description={t('missions.dueDescription')}
              missions={groups.due}
              dueIds={dueIds}
              draftIds={draftIds}
              emptyTitle={t('missions.emptyTitle')}
              emptyBody={t('missions.emptyBody')}
              onStart={startMission}
            />
            <MissionSection
              title={t('missions.onboarding')}
              description={t('missions.onboardingDescription')}
              missions={groups.onboarding}
              dueIds={dueIds}
              draftIds={draftIds}
              emptyTitle={t('missions.onboardingEmptyTitle')}
              emptyBody={t('missions.onboardingEmptyBody')}
              onStart={startMission}
            />
            <MissionSection
              title={t('missions.challenges')}
              description={t('missions.challengesDescription')}
              missions={groups.challenge}
              dueIds={dueIds}
              draftIds={draftIds}
              emptyTitle={t('missions.challengesEmptyTitle')}
              emptyBody={t('missions.challengesEmptyBody')}
              onStart={startMission}
            />
            {groups.other.length > 0 && (
              <MissionSection
                title={t('missions.otherTitle')}
                description={t('missions.otherDescription')}
                missions={groups.other}
                dueIds={dueIds}
                draftIds={draftIds}
                emptyTitle={t('missions.emptyTitle')}
                emptyBody={t('missions.emptyBody')}
                onStart={startMission}
              />
            )}
            <MissionPaths />
          </div>
        )}
      </div>
    </div>
  );
}

export default MissionCenter;
